import { useState, useEffect, useCallback } from 'react'
import { useAuthStore } from '../hooks/useAuth'
import { apiPost } from '../services/api'
import { X, Clock, AlertTriangle, CheckCircle, Zap } from 'lucide-react'
import toast from 'react-hot-toast'

interface QuizQuestion {
  id: string
  question: string
  options: string[]
  correctAnswer: string
}

export default function RandomQuizPopup() {
  const { user } = useAuthStore()
  const [isVisible, setIsVisible] = useState(false)
  const [quiz, setQuiz] = useState<QuizQuestion | null>(null)
  const [selectedAnswer, setSelectedAnswer] = useState('')
  const [timeLeft, setTimeLeft] = useState(30)
  const [result, setResult] = useState<'correct' | 'incorrect' | null>(null)

  // Random quiz trigger - simulates teacher sending a sudden quiz
  useEffect(() => {
    if (!user || user.role === 'admin') return

    // Check for incoming quiz from server every 10 seconds
    const checkInterval = setInterval(async () => {
      try {
        // In real app: const data = await apiGet('/quizzes/incoming')
        // For demo: randomly trigger quiz (1% chance every 10 seconds)
        if (Math.random() < 0.01 && !isVisible) {
          triggerRandomQuiz()
        }
      } catch {
        // Silent fail
      }
    }, 10000)

    return () => clearInterval(checkInterval)
  }, [user, isVisible])

  const triggerRandomQuiz = () => {
    const questions: QuizQuestion[] = [
      {
        id: 'rq1',
        question: 'What is 7 × 8?',
        options: ['54', '56', '58', '52'],
        correctAnswer: '56'
      },
      {
        id: 'rq2',
        question: 'Which planet is known as the Red Planet?',
        options: ['Venus', 'Mars', 'Jupiter', 'Saturn'],
        correctAnswer: 'Mars'
      },
      {
        id: 'rq3',
        question: 'What is the chemical symbol for Gold?',
        options: ['Go', 'Gd', 'Au', 'Ag'],
        correctAnswer: 'Au'
      },
      {
        id: 'rq4',
        question: 'Who wrote "Romeo and Juliet"?',
        options: ['Charles Dickens', 'William Shakespeare', 'Jane Austen', 'Mark Twain'],
        correctAnswer: 'William Shakespeare'
      },
      {
        id: 'rq5',
        question: 'What is the capital of Japan?',
        options: ['Seoul', 'Beijing', 'Tokyo', 'Bangkok'],
        correctAnswer: 'Tokyo'
      },
    ]

    const randomQ = questions[Math.floor(Math.random() * questions.length)]
    setQuiz(randomQ)
    setIsVisible(true)
    setTimeLeft(30)
    setSelectedAnswer('')
    setResult(null)

    // Play alert sound (browser notification)
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('🚨 SUDDEN QUIZ!', {
        body: 'Answer quickly! You have 30 seconds.',
        icon: '/favicon.svg'
      })
    }
  }

  // Timer for quiz
  useEffect(() => {
    if (!isVisible || result) return

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          handleTimeout()
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [isVisible, result])

  const handleTimeout = () => {
    setResult('incorrect')
    toast.error("Time's up! You failed to answer in time.")
    setTimeout(() => setIsVisible(false), 3000)
  }

  const handleSubmit = () => {
    if (!selectedAnswer || !quiz) return

    const isCorrect = selectedAnswer === quiz.correctAnswer
    setResult(isCorrect ? 'correct' : 'incorrect')

    if (isCorrect) {
      toast.success('Correct! +10 points')
    } else {
      toast.error(`Wrong! The answer was: ${quiz.correctAnswer}`)
    }

    // Report to server
    apiPost('/quizzes/result', {
      questionId: quiz.id,
      answer: selectedAnswer,
      correct: isCorrect,
      timeTaken: 30 - timeLeft
    }).catch(() => {})

    setTimeout(() => setIsVisible(false), 3000)
  }

  if (!isVisible || !quiz) return null

  return (
    <div className="quiz-overlay">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 max-w-lg w-full mx-4 shadow-2xl animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-warning/20 rounded-lg flex items-center justify-center">
              <Zap className="w-4 h-4 text-warning" />
            </div>
            <div>
              <h3 className="font-bold text-lg">🚨 SUDDEN QUIZ!</h3>
              <p className="text-xs text-slate-500">Teacher-triggered random assessment</p>
            </div>
          </div>
          <div className={`flex items-center gap-1 px-3 py-1 rounded-lg text-sm font-bold ${
            timeLeft <= 10 ? 'bg-danger/20 text-danger animate-pulse' : 'bg-slate-800'
          }`}>
            <Clock className="w-4 h-4" />
            {timeLeft}s
          </div>
        </div>

        {/* Warning */}
        <div className="flex items-center gap-2 p-3 bg-warning/10 border border-warning/20 rounded-lg mb-4">
          <AlertTriangle className="w-4 h-4 text-warning shrink-0" />
          <p className="text-xs text-warning">
            Answer immediately! This quiz cannot be skipped and affects your grade.
          </p>
        </div>

        {/* Question */}
        <div className="mb-4">
          <p className="text-lg font-medium mb-4">{quiz.question}</p>
          <div className="space-y-2">
            {quiz.options.map((option, i) => (
              <button
                key={i}
                onClick={() => !result && setSelectedAnswer(option)}
                disabled={!!result}
                className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all text-left ${
                  result && option === quiz.correctAnswer
                    ? 'border-success bg-success/10'
                    : result && selectedAnswer === option && option !== quiz.correctAnswer
                    ? 'border-danger bg-danger/10'
                    : selectedAnswer === option
                    ? 'border-primary bg-primary/10'
                    : 'border-slate-700 hover:border-slate-600 bg-slate-800/50'
                }`}
              >
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  selectedAnswer === option ? 'border-primary' : 'border-slate-600'
                }`}>
                  {selectedAnswer === option && <div className="w-2.5 h-2.5 bg-primary rounded-full" />}
                </div>
                <span>{option}</span>
                {result && option === quiz.correctAnswer && (
                  <CheckCircle className="w-4 h-4 text-success ml-auto" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Submit */}
        {!result && (
          <button
            onClick={handleSubmit}
            disabled={!selectedAnswer}
            className="w-full py-3 bg-primary hover:bg-primary-dark rounded-lg font-medium transition-colors disabled:opacity-50"
          >
            Submit Answer
          </button>
        )}

        {result && (
          <div className={`text-center py-3 rounded-lg font-medium ${
            result === 'correct' ? 'bg-success/20 text-success' : 'bg-danger/20 text-danger'
          }`}>
            {result === 'correct' ? '✅ Correct! Well done!' : '❌ Wrong answer!'}
          </div>
        )}
      </div>
    </div>
  )
}
